import { Link, useLocation } from "react-router-dom";
import { Home, MessageSquare, PlusCircle, User } from "lucide-react";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { useAuth } from "@/contexts/AuthContext";
import { useIsArtisan } from "@/hooks/useArtisanProfile";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const ClientBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const unreadCount = useUnreadCount();
  const { data: isArtisan } = useIsArtisan();
  const lPath = useLocalizedPath();

  if (!user || isArtisan) return null;

  const stripSlash = (p: string) => p.replace(/\/$/, "") || "/";
  const isActive = (path: string) => stripSlash(location.pathname) === stripSlash(path);

  const navItems = [
    { to: lPath("/"), icon: Home, label: "Accueil" },
    { to: lPath("/poster-annonce"), icon: PlusCircle, label: "Poster", isPrimary: true },
    {
      to: lPath("/messagerie"),
      icon: MessageSquare,
      label: "Messages",
      badge: unreadCount > 0 ? (unreadCount > 99 ? "99+" : String(unreadCount)) : undefined,
    },
    { to: lPath("/mon-profil"), icon: User, label: "Profil" },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/70 bg-card/97 backdrop-blur-md safe-area-bottom md:hidden"
      aria-label="Navigation principale"
    >
      <div className="flex items-stretch justify-around h-16">
        {navItems.map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center py-2 px-1 min-h-[44px] transition-colors",
                active ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
              aria-label={item.label + (item.badge ? ` (${item.badge} non lu${Number(item.badge) > 1 ? "s" : ""})` : "")}
              aria-current={active ? "page" : undefined}
            >
              {/* Active pill indicator */}
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-primary" aria-hidden="true" />
              )}

              <div className="relative">
                <item.icon
                  className={cn(
                    "transition-all duration-150",
                    active ? "h-6 w-6" : "h-5 w-5",
                    item.isPrimary && !active ? "text-primary" : ""
                  )}
                  aria-hidden="true"
                />
                {item.badge && (
                  <Badge
                    className="absolute -top-2 -right-3 h-4 min-w-4 rounded-full bg-destructive px-1 text-[9px] text-destructive-foreground leading-none flex items-center justify-center"
                    aria-hidden="true"
                  >
                    {item.badge}
                  </Badge>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default ClientBottomNav;
